import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {ICommunityDocumentFileModel} from "../../../models/community/documents/ICommunityDocumentFileModel";
import {
    CommunityDocumentNature,
    ICommunityDocumentModel
} from "../../../models/community/documents/ICommunityDocumentModel";
import {ICommunityDocumentContentModel} from "../../../models/community/documents/ICommunityDocumentContentModel";
import {ICommunityDocumentUploadModel} from "../../../models/community/documents/ICommunityDocumentUploadModel";
import {ICommunityDocumentSearchModel} from "../../../models/community/documents/ICommunityDocumentSearchModel";
import {ICommunityDocumentSearchOutgoing} from "../../../models/community/documents/ICommunityDocumentSearchOutgoing";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";
import {Services} from "../../Services";
import {Resources} from "../../../resources/Resources";

export class CommunityDocumentsService {

    public static readonly DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx", "odt", "rtf", "html", "htm"];
    public static readonly UPLOAD_EXTENSIONS = ["pdf", "doc", "docx", "odt", "rtf", "html", "htm", "xls", "xlsx", "ods", "ppt", "pptx", "odp", "txt", "md", "csv", "tsv", "json", "xml"];
    public static readonly UPLOAD_MAX_SIZE = 10 * 1024 * 1024;

    public static updateEvents: ((document: ICommunityDocumentModel) => void)[] = [];
    public static deleteEvents: ((id: string) => void)[] = [];

    public static dispose(): void {
        this.updateEvents = [];
        this.deleteEvents = [];
    }

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_DOCUMENT_UPDATE, (data: ICommunityDocumentModel) => {
            this.updateEvents.forEach(value => value(data));
        });
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_DOCUMENT_DELETE, (data: { id: string }) => {
            this.deleteEvents.forEach(value => value(data.id));
        });
    }

    static onUpdate(component: INetworkComponent, func: (document: ICommunityDocumentModel) => void) {
        this.updateEvents.push(func);
        component.onRemove(() => arrayRemove(this.updateEvents, func));
    }

    static onDelete(component: INetworkComponent, func: (id: string) => void) {
        this.deleteEvents.push(func);
        component.onRemove(() => arrayRemove(this.deleteEvents, func));
    }

    public static async uploadFile(communityId: string, file: File, component?: INetworkComponent, onProgress?: (percent: number) => void): Promise<ICommunityDocumentFileModel | undefined> {
        const request = await Network.postFormData(ProductType.COMMUNITY, `/community/documents/${communityId}/files`, {file: file}, component, {
            onUploadProgress: (event) => onProgress?.(event.total ? event.loaded * 100 / event.total : 0)
        });
        return request.status == HttpStatus.OK ? request.data as ICommunityDocumentFileModel : undefined;
    }

    public static async tree(communityId: string, component?: INetworkComponent): Promise<ICommunityDocumentModel[] | undefined> {
        const request = await Network.get(ProductType.COMMUNITY, `/community/documents/${communityId}`, component);
        return request?.status == HttpStatus.OK ? request.data as ICommunityDocumentModel[] : undefined;
    }

    public static async content(communityId: string, documentId: string, version: number | undefined, component?: INetworkComponent): Promise<ICommunityDocumentContentModel | undefined> {
        const suffix = version ? `?version=${version}` : "";
        const request = await Network.get(ProductType.COMMUNITY, `/community/documents/${communityId}/${documentId}/content${suffix}`, component);
        return request?.status == HttpStatus.OK ? request.data as ICommunityDocumentContentModel : undefined;
    }

    public static async original(communityId: string, documentId: string, component?: INetworkComponent): Promise<Blob | undefined> {
        const request = await Network.getBlob(ProductType.COMMUNITY, `/community/documents/${communityId}/${documentId}/original`, component);
        return request?.status == HttpStatus.OK ? request.data as Blob : undefined;
    }

    public static async upload(communityId: string, files: File[], path: string, nature: CommunityDocumentNature, onProgress: (percent: number) => void, component: INetworkComponent): Promise<ICommunityDocumentUploadModel | undefined> {
        this.silence(component);
        const request = await Network.postFormData(ProductType.COMMUNITY, `/community/documents/${communityId}/upload`, {
            files: files,
            path: path,
            nature: nature
        }, component, {
            onUploadProgress: (event) => onProgress(event.total ? Math.round(event.loaded * 100 / event.total) : 0)
        });
        if (request?.status == HttpStatus.BAD_REQUEST) {
            return {documents: [], errors: files.map(file => ({name: file.name, error: request.data?.message ?? ""}))};
        }
        return this.result(request);
    }

    public static async addLink(communityId: string, url: string, path: string, nature: CommunityDocumentNature, name: string | undefined, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.postJson(ProductType.COMMUNITY, `/community/documents/${communityId}/links`, {
            url: url,
            path: path,
            nature: nature,
            name: name
        }, component);
        return this.result(request);
    }

    public static async createText(communityId: string, path: string, name: string, nature: CommunityDocumentNature, html: string, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.postJson(ProductType.COMMUNITY, `/community/documents/${communityId}/texts`, {
            path: path,
            name: name,
            nature: nature,
            html: html
        }, component);
        return this.result(request);
    }

    public static async updateContent(communityId: string, documentId: string, html: string, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.putJson(ProductType.COMMUNITY, `/community/documents/${communityId}/${documentId}/content`, {html: html}, component);
        return this.result(request);
    }

    public static async rename(communityId: string, id: string, name: string, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.putJson(ProductType.COMMUNITY, `/community/documents/${communityId}/${id}/rename`, {name: name}, component);
        return this.result(request);
    }

    public static async move(communityId: string, id: string, path: string, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.putJson(ProductType.COMMUNITY, `/community/documents/${communityId}/${id}/move`, {path: path}, component);
        return this.result(request);
    }

    public static async nature(communityId: string, id: string, nature: CommunityDocumentNature, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.putJson(ProductType.COMMUNITY, `/community/documents/${communityId}/${id}/nature`, {nature: nature}, component);
        return this.result(request);
    }

    public static async delete(communityId: string, id: string, recursive: boolean, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.delete(ProductType.COMMUNITY, `/community/documents/${communityId}/${id}${recursive ? "?recursive=true" : ""}`, component);
        return this.result(request);
    }

    public static async refresh(communityId: string, id: string, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.postJson(ProductType.COMMUNITY, `/community/documents/${communityId}/${id}/refresh`, {}, component);
        return this.result(request);
    }

    public static async createFolder(communityId: string, path: string, name: string, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.postJson(ProductType.COMMUNITY, `/community/documents/${communityId}/folders`, {
            path: path,
            name: name,
            usedForWriting: true
        }, component);
        return this.result(request);
    }

    public static async writing(communityId: string, folderId: string, usedForWriting: boolean, component: INetworkComponent): Promise<ICommunityDocumentModel | undefined> {
        this.silence(component);
        const request = await Network.putJson(ProductType.COMMUNITY, `/community/documents/${communityId}/${folderId}/writing`, {usedForWriting: usedForWriting}, component);
        return this.result(request);
    }

    public static async search(communityId: string, search: ICommunityDocumentSearchOutgoing, component?: INetworkComponent): Promise<ICommunityDocumentSearchModel | undefined> {
        this.silence(component);
        const request = await Network.postJson(ProductType.COMMUNITY, `/community/documents/${communityId}/search`, search, component);
        return this.result(request);
    }

    public static errorLabel(key: string): string {
        const translationKey = "words.brandDocumentError_" + key.replace(/\./g, "_");
        const label = Resources.t(translationKey);
        return label == translationKey ? Resources.t("words.errorGenericErrorMessage") : label;
    }

    private static silence(component?: INetworkComponent) {
        if (component && !component.networkErrorsHandler?.find(value => value.status == HttpStatus.BAD_REQUEST && value.message == "none")) {
            Services.handleErrors(component, [{status: HttpStatus.BAD_REQUEST, message: "none"}]);
        }
    }

    private static result<T>(request: any): T | undefined {
        if (request?.status == HttpStatus.OK) {
            return request.data as T;
        }
        if (request?.status == HttpStatus.BAD_REQUEST) {
            const key = request.data?.message;
            Network.router.static.components.notifications.notify(this.errorLabel(key ?? ""), undefined, 4000, "error");
        }
        return undefined;
    }
}
